/** Derived view only: original label and stock events are never mutated. */
export interface CorrectableLabel {
  payload: { lp_code?: string | null; expires_at?: string | null; data: Record<string, unknown> };
}
export interface CorrectionStockEvent {
  id?: unknown; rev?: unknown; client_ts?: unknown;
  payload: Record<string, unknown>;
}
interface LotCorrectionPatch {
  expiresAt?: string;
  data: Record<string, unknown>;
}
export interface LotCorrectionIndex {
  identities: Map<string, Set<string>>;
  corrections: Map<string, LotCorrectionPatch>;
}
const lotKey = (owner: string, lot: string): string => JSON.stringify([owner, lot]);

/** Build once per stock snapshot; keep ambiguous outputs and stable correction ordering. */
export function buildLotCorrectionIndex(events: CorrectionStockEvent[]): LotCorrectionIndex {
  const identities = new Map<string, Set<string>>();
  const groups = new Map<string, CorrectionStockEvent[]>();
  for (const event of events) {
    const p = event.payload;
    if (typeof p.food_id !== 'string' || typeof p.batch_lot !== 'string') continue;
    if (typeof p.source_production_id === 'string') {
      const key = lotKey(p.source_production_id, p.batch_lot);
      const foods = identities.get(key) ?? new Set<string>();
      foods.add(p.food_id);
      identities.set(key, foods);
    }
    if (p.lot_state_action === 'update_metadata' || p.movement_class === 'inventory_adjustment') {
      const key = lotKey(p.food_id, p.batch_lot);
      const group = groups.get(key) ?? [];
      group.push(event);
      groups.set(key, group);
    }
  }
  const corrections = new Map<string, LotCorrectionPatch>();
  for (const [key, group] of groups) {
    const patch: LotCorrectionPatch = { data: {} };
    for (const event of group.sort(order)) {
      const p = event.payload;
      if (p.lot_state_action === 'update_metadata' && typeof p.expiration_date === 'string') {
        patch.expiresAt = p.expiration_date;
      }
      if (p.movement_class === 'inventory_adjustment' && typeof p.inventory_counted_qty === 'number') {
        patch.data.corrected_quantity = p.inventory_counted_qty;
        patch.data.corrected_uom = p.stock_uom ?? p.uom;
      }
      patch.data.lot_correction_reason = p.reason;
      patch.data.lot_correction_event_id = event.id;
    }
    corrections.set(key, patch);
  }
  return { identities, corrections };
}

export function labelStockIdentity(label: CorrectableLabel, events: CorrectionStockEvent[] | LotCorrectionIndex): { foodId: string; lot: string } | null {
  const production = label.payload.data.production_id;
  const lot = label.payload.data.material_lot_code ?? label.payload.lp_code;
  if (typeof production !== 'string' || typeof lot !== 'string') return null;
  const foods = Array.isArray(events)
    ? new Set(events.filter(({ payload: p }) => p.source_production_id === production
      && p.batch_lot === lot && typeof p.food_id === 'string').map(({ payload: p }) => p.food_id as string))
    : events.identities.get(lotKey(production, lot));
  return foods?.size === 1 ? { foodId: [...foods][0], lot } : null;
}
function order(a: CorrectionStockEvent, b: CorrectionStockEvent): number {
  const ar = typeof a.rev === 'number' && a.rev > 0 ? a.rev : null;
  const br = typeof b.rev === 'number' && b.rev > 0 ? b.rev : null;
  if (ar !== null && br !== null) return ar - br;
  if (ar !== null) return -1; // pending local corrections follow acknowledged facts
  if (br !== null) return 1;
  return String(a.client_ts ?? '').localeCompare(String(b.client_ts ?? ''));
}
export function applyLotCorrections<T extends CorrectableLabel>(label: T, events: CorrectionStockEvent[] | LotCorrectionIndex): T {
  const identity = labelStockIdentity(label, events);
  if (!identity) return label;
  if (!Array.isArray(events)) {
    const patch = events.corrections.get(lotKey(identity.foodId, identity.lot));
    if (!patch) return label;
    const data = { ...label.payload.data, ...patch.data };
    if (patch.expiresAt !== undefined) data.original_expires_at ??= label.payload.expires_at;
    return { ...label, payload: { ...label.payload, data, expires_at: patch.expiresAt ?? label.payload.expires_at } };
  }
  const corrections = events.filter(({ payload: p }) => p.food_id === identity.foodId && p.batch_lot === identity.lot
    && (p.lot_state_action === 'update_metadata' || p.movement_class === 'inventory_adjustment')).sort(order);
  if (!corrections.length) return label;
  const data = { ...label.payload.data };
  let expires = label.payload.expires_at;
  for (const e of corrections) {
    const p = e.payload;
    if (p.lot_state_action === 'update_metadata' && typeof p.expiration_date === 'string') {
      data.original_expires_at ??= label.payload.expires_at;
      expires = p.expiration_date;
    }
    if (p.movement_class === 'inventory_adjustment' && typeof p.inventory_counted_qty === 'number') {
      data.corrected_quantity = p.inventory_counted_qty;
      data.corrected_uom = p.stock_uom ?? p.uom;
    }
    data.lot_correction_reason = p.reason;
    data.lot_correction_event_id = e.id;
  }
  return { ...label, payload: { ...label.payload, data, expires_at: expires } };
}
export interface LotCorrectionPrintRequest {
  id: string; tenantId: string; businessId: string; foodId: string; lot: string | null;
  copies: number; reason: string; date?: string; quantity?: number; uom?: string;
}
